"use client";

import * as React from "react";
import { Bar, BarChart, CartesianGrid, Label, Pie, PieChart, XAxis, YAxis, Sector } from "recharts";
import { PieSectorDataItem } from "recharts/types/polar/Pie";
import { ArrowRight, Download, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent, type ChartConfig } from "@/components/ui/chart";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const allocationData = [{
  sector: "entrepreneurship",
  value: 45,
  fill: "var(--color-chart-1)"
}, {
  sector: "education",
  value: 25,
  fill: "var(--color-chart-2)"
}, {
  sector: "workforce",
  value: 20,
  fill: "var(--color-chart-3)"
}, {
  sector: "leadership",
  value: 10,
  fill: "var(--color-chart-4)"
}];
const allocationConfig = {
  value: {
    label: "Allocation"
  },
  entrepreneurship: {
    label: "Entrepreneurship",
    color: "var(--color-chart-1)"
  },
  education: {
    label: "Youth & Education",
    color: "var(--color-chart-2)"
  },
  workforce: {
    label: "Workforce Innovation",
    color: "var(--color-chart-3)"
  },
  leadership: {
    label: "Leadership",
    color: "var(--color-chart-4)"
  }
} satisfies ChartConfig;
const growthData = [{
  year: "2020",
  revenue: 1.2
}, {
  year: "2021",
  revenue: 1.8
}, {
  year: "2022",
  revenue: 2.4
}, {
  year: "2023",
  revenue: 3.1
}, {
  year: "2024",
  revenue: 4.2
}];
const growthConfig = {
  revenue: {
    label: "Revenue (in Millions)",
    color: "var(--color-chart-2)"
  }
} satisfies ChartConfig;
export default function DataThatDrivesChange() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const totalAllocation = React.useMemo(() => {
    return allocationData.reduce((acc, curr) => acc + curr.value, 0);
  }, []);
  return <section id="data-that-drives-change" ref={ref} className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/30 -z-10 skew-x-12 translate-x-20" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10" />

      <div className="w-full max-w-7xl mx-auto px-6">
        
        {}
        <div className={`mb-16 max-w-3xl transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-12 bg-secondary inline-block" />
            <span className="text-secondary font-bold tracking-widest uppercase text-sm">
              Impact Analytics
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-6 leading-tight">
            Data That Drives Change
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We believe in transparency and measurable outcomes. Our strategic investments are 
            accelerating economic growth and creating sustainable opportunities for the 
            Hispanic community across Colorado.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <div className={`lg:col-span-5 flex flex-col gap-6 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="bg-card border border-border/50 rounded-xl shadow-lg p-6 md:p-8 relative overflow-hidden group hover:shadow-xl transition-shadow duration-300">
              <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
              
              <div className="mb-6">
                <h3 className="text-2xl font-serif font-bold text-primary">Program Allocation</h3>
                <p className="text-sm text-muted-foreground mt-1">Distribution of funds across key initiatives</p>
              </div>

              <ChartContainer config={allocationConfig} className="mx-auto aspect-square max-h-[300px]">
                <PieChart>
                  <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                  <Pie data={allocationData} dataKey="value" nameKey="sector" innerRadius={60} outerRadius={100} strokeWidth={5} stroke="hsl(var(--background))">
                    <Label content={({
                    viewBox
                  }) => {
                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                      return <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                              <tspan x={viewBox.cx} y={viewBox.cy} className="fill-primary text-3xl font-bold font-serif">
                                {totalAllocation}%
                              </tspan>
                              <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground text-xs font-sans uppercase tracking-wider">
                                Committed
                              </tspan>
                            </text>;
                    }
                  }} />
                  </Pie>
                  <ChartLegend content={<ChartLegendContent nameKey="sector" />} className="-translate-y-2 flex-wrap gap-2 [&>*]:basis-1/4 [&>*]:justify-center" />
                </PieChart>
              </ChartContainer>
              
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-start gap-3">
                  <div className="bg-secondary/10 p-2 rounded-full text-secondary-foreground">
                    <TrendingUp className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-primary">Strategic Focus</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Nearly half of our resources are dedicated to direct entrepreneurship support, 
                      fueling the engine of our local economy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-7 flex flex-col gap-6 transition-all duration-700 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="bg-card border border-border/50 rounded-xl shadow-lg p-6 md:p-8 relative overflow-hidden group hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
              <div className="absolute top-0 left-0 w-1 h-full bg-secondary" />
              
              <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-serif font-bold text-primary">Community Revenue Growth</h3>
                  <p className="text-sm text-muted-foreground mt-1">Year-over-year revenue of supported businesses</p>
                </div>
                <div className="text-right hidden sm:block">
                  <span className="text-3xl font-bold text-secondary block">+250%</span>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">5-Year Growth</span>
                </div>
              </div>

              <ChartContainer config={growthConfig} className="w-full h-[350px]">
                <BarChart accessibilityLayer data={growthData} margin={{
                top: 20,
                right: 0,
                left: 0,
                bottom: 0
              }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="year" tickLine={false} tickMargin={10} axisLine={false} tickFormatter={value => value} className="text-xs font-medium text-muted-foreground" />
                  <YAxis tickLine={false} axisLine={false} tickMargin={10} tickFormatter={value => `$${value}M`} className="text-xs font-medium text-muted-foreground" />
                  <ChartTooltip cursor={{
                  fill: "var(--color-muted)",
                  opacity: 0.2
                }} content={<ChartTooltipContent indicator="line" />} />
                  <Bar dataKey="revenue" fill="var(--color-secondary)" radius={[4, 4, 0, 0]} barSize={40}>
                    <LabelList dataKey="revenue" position="top" formatter={(val: number) => `$${val}M`} className="fill-primary font-bold text-xs" />
                  </Bar>
                </BarChart>
              </ChartContainer>

              <div className="mt-auto pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground italic">
                  *Data reflects aggregated revenue reported by program participants.
                </p>
                <Button variant="outline" className="w-full sm:w-auto border-primary/20 text-primary hover:bg-primary hover:text-white group">
                  View Full Report
                  <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className={`mt-12 flex flex-col md:flex-row items-center justify-between bg-primary text-primary-foreground p-8 rounded-xl shadow-2xl transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-6 md:mb-0 md:mr-8">
            <h4 className="text-2xl font-serif font-bold mb-2 text-white">Transparency is our priority.</h4>
            <p className="text-primary-foreground/80 max-w-xl">
              Download our latest Annual Report to see exactly how every dollar is invested in the community.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-bold px-8 py-6 h-auto text-base shadow-lg" asChild>
              <Link to="#" newTab>
                <Download className="mr-2 size-5" />
                Download 2024 Report
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}
import { LabelList } from "recharts";